import {
  AI_BP_LABEL,
  BASELINE_LABEL,
  CRITICAL_HIGH_BUCKET,
  MEDIUM_LOW_BUCKET,
  SECURE_SCORE_LABEL,
} from "./ruleSet";

/** The posture tab bar's entries; rendered by PostureShell. */
export interface PostureTab {
  to: string;
  label: string;
  /** The last path segment; the tab's data-testid is `posture-tab-${id}`. */
  id: string;
}

/**
 * The four buckets (D27), in the overview's order. Each is one tab: the two
 * rule-set buckets show their score, chips and findings; AI best practice and
 * Microsoft Secure Score show their own views, or exactly "Switched off" when
 * switched off (D25). The tabs are always there — a switched-off framework is
 * shown as switched off, never hidden.
 */
export const BUCKET_TABS: readonly PostureTab[] = [
  { to: CRITICAL_HIGH_BUCKET.path, label: CRITICAL_HIGH_BUCKET.label, id: "critical-high" },
  { to: MEDIUM_LOW_BUCKET.path, label: MEDIUM_LOW_BUCKET.label, id: "medium-low" },
  { to: "/posture/ai-bp", label: AI_BP_LABEL, id: "ai-bp" },
  { to: "/posture/secure-score", label: SECURE_SCORE_LABEL, id: "secure-score" },
];

/**
 * The secondary views, set apart from the buckets: the whole run, the
 * Microsoft baseline (a framework, not a bucket), the manual lane and the
 * accepted risks.
 */
export const SECONDARY_TABS: readonly PostureTab[] = [
  { to: "/posture/all", label: "All findings", id: "all" },
  { to: "/posture/baseline", label: BASELINE_LABEL, id: "baseline" },
  { to: "/posture/manual", label: "Manual attestations", id: "manual" },
  { to: "/posture/exceptions", label: "Exceptions", id: "exceptions" },
];

/** The default tab: the first bucket. */
export const DEFAULT_POSTURE_TAB = CRITICAL_HIGH_BUCKET.path;

/**
 * The legacy `?tab=` values and the tab each one names. The old "All
 * findings" tab lived at /posture itself; the others keep their paths.
 */
const TAB_ALIASES: Record<string, string> = {
  "critical-high": CRITICAL_HIGH_BUCKET.path,
  critical_high: CRITICAL_HIGH_BUCKET.path,
  own_base: CRITICAL_HIGH_BUCKET.path,
  "medium-low": MEDIUM_LOW_BUCKET.path,
  medium_low: MEDIUM_LOW_BUCKET.path,
  own_lower: MEDIUM_LOW_BUCKET.path,
  "ai-bp": "/posture/ai-bp",
  ai_bp: "/posture/ai-bp",
  "secure-score": "/posture/secure-score",
  secure_score: "/posture/secure-score",
  all: "/posture/all",
  findings: "/posture/all",
  baseline: "/posture/baseline",
  ms_baseline: "/posture/baseline",
  manual: "/posture/manual",
  exceptions: "/posture/exceptions",
  cis: "/posture/all",
};

/** The findings-filter parameters of the old All findings tab at /posture. */
const FINDINGS_PARAMS = ["state", "severity", "category", "framework", "reason", "q", "page", "size"];

/**
 * Where a request for /posture itself goes, keeping its query string.
 *
 *  - `?tab=<name>` names a tab: that tab (the parameter is dropped).
 *  - Any findings filter (`?state=fail`, `?reason=…`) is a link to the old All
 *    findings tab, which lived at /posture — the overview's state tiles, the
 *    capability gaps and every bookmark still send them — so it lands on All
 *    findings with the filter intact, never on a bucket that would narrow it.
 *  - Otherwise the default tab, Critical & high.
 */
export function postureIndexTarget(search: string): string {
  const params = new URLSearchParams(search);
  const tab = params.get("tab");
  params.delete("tab");
  let to: string;
  if (tab !== null && TAB_ALIASES[tab] !== undefined) {
    to = TAB_ALIASES[tab];
  } else if (FINDINGS_PARAMS.some((k) => params.has(k))) {
    to = "/posture/all";
  } else {
    to = DEFAULT_POSTURE_TAB;
  }
  const rest = params.toString();
  return rest === "" ? to : `${to}?${rest}`;
}
